/**
 * Thin fetch wrapper. All calls are same-origin (Apache proxies /api/ to Node)
 * with cookie credentials. Error responses from AppError-shaped backend become
 * thrown ApiError objects the UI can render.
 */
export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number, public details?: unknown) {
    super(message);
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

async function request<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;
  const qs = query
    ? '?' +
      new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : '';

  const init: RequestInit = {
    method: options.method ?? (body ? 'POST' : 'GET'),
    credentials: 'include',
    headers: {
      ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body:
      body === undefined
        ? undefined
        : body instanceof FormData
          ? body
          : JSON.stringify(body),
    ...rest,
  };

  const res = await fetch(`/api/v1${path}${qs}`, init);
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = undefined;
  }

  if (!res.ok) {
    const err = (parsed as { error?: { code: string; message: string; details?: unknown } } | undefined)?.error;
    throw new ApiError(
      err?.code ?? 'HTTP_' + res.status,
      err?.message ?? res.statusText,
      res.status,
      err?.details,
    );
  }
  return (parsed as { data?: T } | undefined)?.data as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<ApiOptions, 'body' | 'method'>) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<ApiOptions, 'method'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<ApiOptions, 'method'>) => request<T>(path, { ...opts, method: 'DELETE' }),
};
